import {useWFHooks} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/context';
import {
  projectIdFromParts,
  useProjectStats,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/tsDataModelHooks';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {convertBytes} from '@wandb/weave/util';
import React, {useMemo} from 'react';

import {StatItem} from './components/StatItem';

interface StatsOverviewProps {
  entityName: string;
  projectName: string;
}

export const StatsOverview: React.FC<StatsOverviewProps> = ({
  entityName,
  projectName,
}) => {
  const {useCallsStats} = useWFHooks();
  // Fetch call statistics including total count and storage size
  const {result: callsStats, loading: callsStatsLoading} = useCallsStats({
    entity: entityName,
    project: projectName,
    includeTotalStorageSize: true,
  });

  // Fetch project-wide statistics for storage breakdowns
  const {value: projectStats, loading: projectStatsLoading} = useProjectStats(
    projectIdFromParts({entity: entityName, project: projectName})
  );

  const traceCount = useMemo(() => {
    return callsStats?.count ?? 0;
  }, [callsStats]);

  const callStatsStorageSize = useMemo(() => {
    return convertBytes(callsStats?.total_storage_size_bytes ?? 0);
  }, [callsStats]);

  const storageSize = useMemo(() => {
    return convertBytes(projectStats?.trace_storage_size_bytes ?? 0);
  }, [projectStats]);

  const objectsStorageSize = useMemo(() => {
    return convertBytes(projectStats?.objects_storage_size_bytes ?? 0);
  }, [projectStats]);

  const tablesStorageSize = useMemo(() => {
    return convertBytes(projectStats?.tables_storage_size_bytes ?? 0);
  }, [projectStats]);

  return (
    <Tailwind>
      <div className="mb-32">
        <h3 className="mb-16 text-sm font-semibold uppercase tracking-wider text-moon-500">
          Project Statistics
        </h3>
        <div className="grid grid-cols-2 gap-12 rounded-xl border border-moon-250 bg-gradient-to-b from-white to-moon-50 p-16 shadow-sm sm:grid-cols-3 sm:gap-16 sm:p-20 lg:grid-cols-5 lg:gap-20 lg:p-28">
          <StatItem
            label="Total traces"
            icon="running-repeat"
            value={traceCount.toLocaleString()}
            loading={callsStatsLoading}
          />
          <StatItem
            label="Traces storage"
            icon="database-artifacts"
            value={storageSize}
            loading={projectStatsLoading}
          />
          <StatItem
            label="Objects storage"
            icon="cube-container"
            value={objectsStorageSize}
            loading={projectStatsLoading}
          />
          <StatItem
            label="Tables storage"
            icon="table"
            value={tablesStorageSize}
            loading={projectStatsLoading}
          />
          <StatItem
            label="Calls storage"
            icon="versions-layers"
            value={callStatsStorageSize}
            loading={callsStatsLoading}
          />
        </div>
      </div>
    </Tailwind>
  );
};
